<script setup>
import {
  defineEmits,
  defineProps,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
} from "vue";
import apiClient from "@/apiClient";
import * as Icon from "@heroicons/vue/24/solid";

const props = defineProps({
  risk: { type: Object, required: true },
  quoteId: { type: [Number, String], required: false },
  ticketId: { type: [Number, String], required: false },
  contextTicketIds: { type: Array, required: false },
  entries: { type: Object, required: false },
  editable: { type: Boolean, required: false },
});

const emit = defineEmits([
  "update:risk",
  "update:entries",
  "update:description",
  "add:address",
]);

const updateFields = (updates) => {
  emit("update:risk", { ...(props.risk ?? {}), ...updates });
};

const updateField = (field, value) => {
  updateFields({ [field]: value });
};

const inputClass =
  "w-full p-2 rounded border bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100";
const selectClass = inputClass;
const textareaClass = inputClass;

const chatOpen = ref(false);
const chatInput = ref("");
const chatLoading = ref(false);
const chatError = ref(null);
const chatMessages = ref([]);
const chatPosition = ref({ x: null, y: null });
const dragging = ref(false);
const dragOffset = ref({ x: 0, y: 0 });
const chatWindowRef = ref(null);
const chatScrollRef = ref(null);
const eventTypes = ref([]);
const pendingEventType = ref(null);
const normalizedContextTicketIds = () =>
  Array.isArray(props.contextTicketIds)
    ? props.contextTicketIds
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id))
    : [];

const buildTicketContextPayload = () => {
  const ticketIds = normalizedContextTicketIds();
  if (ticketIds.length) {
    return { ticket_ids: ticketIds };
  }

  const fallbackTicketId = Number(props.ticketId);
  if (!Number.isNaN(fallbackTicketId)) {
    return { ticket_id: fallbackTicketId };
  }

  return {};
};

const parseYesNoBoolean = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value;
  const normalized = String(value).trim().toLowerCase();
  if (["yes", "true", "1", "y"].includes(normalized)) return true;
  if (["no", "false", "0", "n"].includes(normalized)) return false;
  return null;
};

const resolveEventTypeId = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "object") {
    if ("id" in value) return resolveEventTypeId(value.id);
    if ("name" in value) return resolveEventTypeId(value.name);
  }
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const numeric = Number(trimmed);
  if (!Number.isNaN(numeric)) return numeric;
  const needle = trimmed.toLowerCase();
  const exact = eventTypes.value.find(
    (type) => type.name?.toLowerCase() === needle,
  );
  if (exact) return exact.id;
  const contains = eventTypes.value.find((type) =>
    needle.includes(type.name?.toLowerCase() ?? ""),
  );
  if (contains) return contains.id;
  const partial = eventTypes.value.find((type) =>
    (type.name ?? "").toLowerCase().includes(needle),
  );
  return partial?.id ?? null;
};

const applyEventType = (payload) => {
  const resolved = resolveEventTypeId(payload);
  if (resolved !== null) {
    updateFields({ event_type_id: resolved });
    return;
  }

  if (!eventTypes.value.length) {
    if (typeof payload === "string" && payload.trim()) {
      pendingEventType.value = payload.trim();
    } else if (payload && typeof payload === "object" && "name" in payload) {
      const name = String(payload.name ?? "").trim();
      if (name) pendingEventType.value = name;
    }
  }
};

const loadEventTypes = async () => {
  try {
    const response = await apiClient.get("/api/event-types");
    eventTypes.value = response.data ?? [];
  } catch (error) {
    eventTypes.value = [];
  }

  if (pendingEventType.value) {
    applyEventType(pendingEventType.value);
    pendingEventType.value = null;
  }
};

onMounted(() => {
  loadEventTypes();
});

const openChatWithHistory = async () => {
  openChat();
  await loadConversation();
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const ensureChatPosition = async () => {
  if (window.innerWidth < 640) {
    chatPosition.value = { x: 0, y: 0 };
    return;
  }
  const padding = 16;
  await nextTick();
  const rect = chatWindowRef.value?.getBoundingClientRect();
  const width = rect?.width ?? 520;
  const height = rect?.height ?? 520;
  const fallbackX = window.innerWidth - width - padding;
  const fallbackY = window.innerHeight - height - padding;
  const currentX = chatPosition.value.x ?? fallbackX;
  const currentY = chatPosition.value.y ?? fallbackY;
  chatPosition.value = {
    x: clamp(currentX, padding, window.innerWidth - width - padding),
    y: clamp(currentY, padding, window.innerHeight - height - padding),
  };
};

const openChat = () => {
  chatOpen.value = true;
  chatError.value = null;
  ensureChatPosition();
};

const closeChat = () => {
  chatOpen.value = false;
};

const parseJsonBlock = (text) => {
  if (!text) return { message: "", json: null };
  const jsonStart = text.indexOf("{");
  const jsonEnd = text.lastIndexOf("}");
  if (jsonStart === -1 || jsonEnd === -1 || jsonEnd <= jsonStart) {
    return { message: text.trim(), json: null };
  }
  const message = text.slice(0, jsonStart).trim();
  const json = text.slice(jsonStart, jsonEnd + 1);
  return { message, json };
};

const applyAssessment = (text) => {
  if (!text) return;
  updateField("ai_assessment", text);

  const { json } = parseJsonBlock(text);
  if (!json) return;
  let parsed = null;
  try {
    parsed = JSON.parse(json);
  } catch (error) {
    return;
  }

  const updates = {};
  if (parsed.event_name) {
    updates.event_name = parsed.event_name;
    emit("update:description", parsed.event_name);
  }
  if (
    parsed.event_type_id ||
    parsed.event_type ||
    parsed.event_type_name ||
    parsed.event_type_label
  ) {
    const payload =
      parsed.event_type_id ??
      parsed.event_type ??
      parsed.event_type_name ??
      parsed.event_type_label;
    applyEventType(payload);
  }
  if (parsed.risk_factors) updates.risk_factors = parsed.risk_factors;
  if (parsed.safety_measures) updates.safety_measures = parsed.safety_measures;
  if (parsed.concerns) updates.concerns = parsed.concerns;
  if (parsed.max_people !== null && parsed.max_people !== undefined) {
    updates.expected_peak_attendance = parsed.max_people;
    updates.max_people = parsed.max_people;
  }
  if (Object.prototype.hasOwnProperty.call(parsed, "food")) {
    const parsedValue = parseYesNoBoolean(parsed.food);
    if (parsedValue !== null) updates.food = parsedValue;
  }
  if (Object.prototype.hasOwnProperty.call(parsed, "alcohol")) {
    const parsedValue = parseYesNoBoolean(parsed.alcohol);
    if (parsedValue !== null) updates.alcohol = parsedValue;
  }
  if (Object.prototype.hasOwnProperty.call(parsed, "treatment_room")) {
    const parsedValue = parseYesNoBoolean(parsed.treatment_room);
    if (parsedValue !== null) updates.treatment_room = parsedValue;
  }
  if (parsed.environment) updates.environment = parsed.environment;
  if (parsed.address) updates.address = parsed.address;
  if (parsed.activities) updates.activities = parsed.activities;
  if (parsed.resources)
    updates.suggested_resources = Array.isArray(parsed.resources)
      ? parsed.resources.join(", ")
      : parsed.resources;
  if (parsed.justification)
    updates.resource_justification = parsed.justification;

  if (Object.keys(updates).length) {
    updateFields(updates);
  }

  if (parsed.date) {
    const location =
      parsed.location_name ||
      parsed.location_address ||
      parsed.location ||
      parsed.address ||
      null;
    const locationName = parsed.location_name ?? null;
    const locationAddress =
      parsed.location_address ?? parsed.location ?? parsed.address ?? null;
    updateEntriesFromDate(parsed.date, {
      location,
      locationName,
      locationAddress,
    });
  }

  const discoveredAddress =
    parsed.location_address ??
    parsed.location ??
    parsed.address ??
    parsed.location_name ??
    null;
  if (discoveredAddress) {
    emit("add:address", discoveredAddress);
  }
};

const applyServerApplied = (applied) => {
  if (!applied || typeof applied !== "object") return false;

  let hasApplied = false;

  const appliedName =
    typeof applied.name === "string" && applied.name.trim()
      ? applied.name.trim()
      : typeof applied.description === "string" && applied.description.trim()
        ? applied.description.trim()
        : null;

  if (appliedName) {
    emit("update:description", appliedName);
    hasApplied = true;
  }

  if (applied.event_risk && typeof applied.event_risk === "object") {
    updateFields(applied.event_risk);
    hasApplied = true;

    const discoveredAddress =
      applied.event_risk.location_address ??
      applied.event_risk.location ??
      applied.event_risk.address ??
      applied.event_risk.location_name ??
      null;
    if (discoveredAddress) {
      emit("add:address", discoveredAddress);
    }
  }

  if (Array.isArray(applied.dates) && props.entries) {
    emit("update:entries", {
      ...(props.entries ?? {}),
      dates: applied.dates,
    });
    hasApplied = true;
  }

  return hasApplied;
};

const sendMessage = async (overrideMessage = null) => {
  const payload =
    typeof overrideMessage === "string"
      ? overrideMessage
      : chatInput.value.trim();
  if (!payload || !props.quoteId) return;
  chatError.value = null;
  chatMessages.value.push({ role: "user", content: payload });
  const placeholderId = Date.now();
  chatMessages.value.push({
    role: "assistant",
    content: "Thinking...",
    id: placeholderId,
  });
  chatInput.value = "";
  chatLoading.value = true;
  await nextTick();
  scrollChatToBottom();

  try {
    const response = await apiClient.post(
      `/api/quote/${props.quoteId}/risk-chat`,
      {
        message: payload,
        ...buildTicketContextPayload(),
      },
    );
    const raw = response.data?.message ?? "";
    const { message: displayMessage } = parseJsonBlock(raw);
    if (Array.isArray(response.data?.history)) {
      chatMessages.value = response.data.history.map((turn) => {
        const parsed =
          turn.role === "assistant"
            ? parseJsonBlock(turn.content)
            : { message: turn.content };
        return {
          role: turn.role,
          content: parsed.message || turn.content,
          raw: turn.content,
          id: `${turn.role}-${Math.random().toString(36).slice(2, 10)}`,
        };
      });
    } else {
      const placeholderIndex = chatMessages.value.findIndex(
        (msg) => msg.id === placeholderId,
      );
      if (placeholderIndex !== -1) {
        chatMessages.value[placeholderIndex].content = displayMessage || raw;
        chatMessages.value[placeholderIndex].raw = raw;
      } else {
        chatMessages.value.push({
          role: "assistant",
          content: displayMessage || raw,
          raw,
        });
      }
    }
    const hasServerApplied = applyServerApplied(response.data?.applied);
    if (!hasServerApplied) {
      applyAssessment(raw);
    } else {
      updateField("ai_assessment", raw);
    }
    await nextTick();
    scrollChatToBottom();
  } catch (error) {
    chatError.value =
      error.response?.data?.message ?? "Unable to contact AI assistant.";
    const placeholderIndex = chatMessages.value.findIndex(
      (msg) => msg.id === placeholderId,
    );
    if (placeholderIndex !== -1) {
      chatMessages.value[placeholderIndex].content =
        "Unable to get a response.";
    }
  } finally {
    chatLoading.value = false;
  }
};

const handleChatKeydown = (event) => {
  if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
    event.preventDefault();
    sendMessage();
  }
};

const assessmentLoading = ref(false);
const assessmentError = ref(null);

const getAssessmentQuestions = (text) => {
  if (!text) return [];
  const { json } = parseJsonBlock(text);
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed.questions) ? parsed.questions : [];
  } catch (error) {
    return [];
  }
};

const startAssessment = async ({ force = false } = {}) => {
  if (!props.quoteId) return;
  await openChatWithHistory();

  if (!force) {
    if (chatMessages.value.length) {
      return;
    }

    if (props.risk?.ai_assessment) {
      return;
    }
  }

  assessmentLoading.value = true;
  assessmentError.value = null;
  const placeholderId = Date.now();
  chatMessages.value.push({
    role: "assistant",
    content: "Thinking...",
    id: placeholderId,
  });
  await nextTick();
  scrollChatToBottom();

  try {
    const response = await apiClient.post(
      `/api/quote/${props.quoteId}/risk-assessment`,
      {
        ...buildTicketContextPayload(),
      },
    );
    const content = response.data?.message ?? "";
    const hasServerApplied = applyServerApplied(response.data?.applied);
    if (!hasServerApplied) {
      applyAssessment(content);
    } else {
      updateField("ai_assessment", content);
    }
    const followUp =
      response.data?.follow_up ??
      (getAssessmentQuestions(content).length
        ? `Risk assessment complete. I still need: ${getAssessmentQuestions(content).join(" ")}`
        : "Risk assessment complete, have any questions?");

    if (Array.isArray(response.data?.history)) {
      chatMessages.value = response.data.history.map((turn) => ({
        role: turn.role,
        content: turn.content,
        id: `${turn.role}-${Math.random().toString(36).slice(2, 10)}`,
      }));
    } else {
      const placeholderIndex = chatMessages.value.findIndex(
        (msg) => msg.id === placeholderId,
      );
      if (placeholderIndex !== -1) {
        chatMessages.value[placeholderIndex].content = followUp;
      } else {
        chatMessages.value.push({ role: "assistant", content: followUp });
      }
    }
  } catch (error) {
    assessmentError.value =
      error.response?.data?.message ?? "Unable to run risk assessment.";
    const placeholderIndex = chatMessages.value.findIndex(
      (msg) => msg.id === placeholderId,
    );
    if (placeholderIndex !== -1) {
      chatMessages.value[placeholderIndex].content =
        "Unable to run the assessment.";
    }
  } finally {
    assessmentLoading.value = false;
    await nextTick();
    scrollChatToBottom();
  }
};

const rescanAssessment = async () => {
  if (!props.quoteId || chatLoading.value || assessmentLoading.value) return;
  await startAssessment({ force: true });
};

const onDragStart = (event) => {
  if (!chatOpen.value || window.innerWidth < 640) return;
  dragging.value = true;
  dragOffset.value = {
    x: event.clientX - (chatPosition.value.x ?? 0),
    y: event.clientY - (chatPosition.value.y ?? 0),
  };
  window.addEventListener("mousemove", onDragMove);
  window.addEventListener("mouseup", onDragEnd);
};

const onDragMove = (event) => {
  if (!dragging.value) return;
  const padding = 8;
  const x = event.clientX - dragOffset.value.x;
  const y = event.clientY - dragOffset.value.y;
  const rect = chatWindowRef.value?.getBoundingClientRect();
  const width = rect?.width ?? 520;
  const height = rect?.height ?? 520;
  chatPosition.value = {
    x: clamp(x, padding, window.innerWidth - width - padding),
    y: clamp(y, padding, window.innerHeight - height - padding),
  };
};

const onDragEnd = () => {
  dragging.value = false;
  window.removeEventListener("mousemove", onDragMove);
  window.removeEventListener("mouseup", onDragEnd);
};

onBeforeUnmount(() => {
  window.removeEventListener("mousemove", onDragMove);
  window.removeEventListener("mouseup", onDragEnd);
});

const loadConversation = async () => {
  if (!props.quoteId || chatLoading.value) return;
  try {
    const response = await apiClient.get(
      `/api/quote/${props.quoteId}/risk-chat`,
    );
    if (Array.isArray(response.data?.history)) {
      chatMessages.value = response.data.history.map((turn) => ({
        role: turn.role,
        content:
          turn.role === "assistant"
            ? parseJsonBlock(turn.content).message || turn.content
            : turn.content,
        raw: turn.content,
        id: `${turn.role}-${Math.random().toString(36).slice(2, 10)}`,
      }));
    }
    await nextTick();
    scrollChatToBottom();
  } catch (error) {
    chatError.value =
      error.response?.data?.message ?? "Unable to load conversation.";
  }
};

const scrollChatToBottom = () => {
  if (!chatScrollRef.value) return;
  chatScrollRef.value.scrollTop = chatScrollRef.value.scrollHeight;
};

const updateEntriesFromDate = (datePayload, location = {}) => {
  if (!props.entries) return;
  const entries = JSON.parse(JSON.stringify(props.entries ?? { dates: [] }));
  const dates = Array.isArray(datePayload) ? datePayload : [datePayload];
  const parsed = dates
    .map((entry) => {
      if (typeof entry === "object" && entry?.date) {
        return {
          date: entry.date,
          start_time: entry.start_time ?? null,
          finish_time: entry.finish_time ?? null,
          location_name: entry.location_name ?? location.locationName ?? null,
          location_address:
            entry.location_address ?? location.locationAddress ?? null,
          resources: entry.resources ?? [],
        };
      }
      if (typeof entry !== "string") return null;
      const normalized = entry.trim();
      const rangeMatch = normalized.match(
        /(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2})(?::\d{2})?\s*-\s*(\d{2}:\d{2})(?::\d{2})?/,
      );
      if (rangeMatch) {
        return {
          date: rangeMatch[1],
          start_time: rangeMatch[2] + ":00",
          finish_time: rangeMatch[3] + ":00",
          location_name: location.locationName ?? null,
          location_address: location.locationAddress ?? null,
          resources: [],
        };
      }
      const match = normalized.match(
        /(\d{4}-\d{2}-\d{2})(?:\s+(\d{2}:\d{2})(?::\d{2})?)?/,
      );
      if (!match) return null;
      return {
        date: match[1],
        start_time: match[2] ? `${match[2]}:00` : null,
        finish_time: null,
        location_name: location.locationName ?? null,
        location_address: location.locationAddress ?? null,
        resources: [],
      };
    })
    .filter(Boolean);

  if (!entries.dates) entries.dates = [];
  if (!parsed.length) return;

  const normalized = (value) => value ?? null;
  parsed.forEach((nextEntry) => {
    const match = entries.dates.find((entry) => {
      if (entry.date !== nextEntry.date) return false;
      const entryStart = normalized(entry.start_time);
      const entryFinish = normalized(entry.finish_time);
      const nextStart = normalized(nextEntry.start_time);
      const nextFinish = normalized(nextEntry.finish_time);
      if (!entryStart && !entryFinish) return true;
      return entryStart === nextStart && entryFinish === nextFinish;
    });
    if (match) {
      if (!match.start_time && nextEntry.start_time) {
        match.start_time = nextEntry.start_time;
      }
      if (!match.finish_time && nextEntry.finish_time) {
        match.finish_time = nextEntry.finish_time;
      }
      if (!match.location_name && nextEntry.location_name) {
        match.location_name = nextEntry.location_name;
      }
      if (!match.location_address && nextEntry.location_address) {
        match.location_address = nextEntry.location_address;
      }
    } else {
      entries.dates.push(nextEntry);
    }
  });

  emit("update:entries", entries);
};

defineExpose({ startAssessment });
</script>

<template>
  <div class="space-y-4">
    <FormBox header="Operational Factors">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-xs text-slate-500">Environment</label>
          <input
            :class="inputClass"
            :value="risk.environment ?? ''"
            :disabled="!editable"
            @input="updateField('environment', $event.target.value)"
          />
        </div>
        <div>
          <label class="block text-xs text-slate-500">Treatment Room</label>
          <select
            :class="selectClass"
            :value="risk.treatment_room"
            :disabled="!editable"
            @change="
              updateField('treatment_room', $event.target.value === 'true')
            "
          >
            <option :value="true">Yes</option>
            <option :value="false">No</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-slate-500">Food</label>
          <select
            :class="selectClass"
            :value="risk.food"
            :disabled="!editable"
            @change="updateField('food', $event.target.value === 'true')"
          >
            <option :value="true">Yes</option>
            <option :value="false">No</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-slate-500">Activities</label>
          <textarea
            :class="textareaClass"
            rows="4"
            :disabled="!editable"
            :value="risk.activities ?? ''"
            @input="updateField('activities', $event.target.value)"
          ></textarea>
        </div>
      </div>
    </FormBox>

    <FormBox header="Risk & Compliance">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-xs text-slate-500">Risk Factors</label>
          <textarea
            :class="textareaClass"
            rows="4"
            :disabled="!editable"
            :value="risk.risk_factors ?? ''"
            @input="updateField('risk_factors', $event.target.value)"
          ></textarea>
        </div>
        <div>
          <label class="block text-xs text-slate-500">Safety Measures</label>
          <textarea
            :class="textareaClass"
            rows="4"
            :disabled="!editable"
            :value="risk.safety_measures ?? ''"
            @input="updateField('safety_measures', $event.target.value)"
          ></textarea>
        </div>
        <div>
          <label class="block text-xs text-slate-500">Concerns</label>
          <textarea
            :class="textareaClass"
            rows="4"
            :disabled="!editable"
            :value="risk.concerns ?? ''"
            @input="updateField('concerns', $event.target.value)"
          ></textarea>
        </div>
        <div>
          <label class="block text-xs text-slate-500"
            >Licensing / Council Requirements</label
          >
          <textarea
            :class="textareaClass"
            rows="4"
            :disabled="!editable"
            :value="risk.licensing_requirements ?? ''"
            @input="updateField('licensing_requirements', $event.target.value)"
          ></textarea>
        </div>
      </div>
    </FormBox>

    <FormBox header="Clinical Notes">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-xs text-slate-500"
            >Clinical Requirements</label
          >
          <textarea
            :class="textareaClass"
            rows="4"
            :disabled="!editable"
            :value="risk.clinical_requirements ?? ''"
            @input="updateField('clinical_requirements', $event.target.value)"
          ></textarea>
        </div>
        <div>
          <label class="block text-xs text-slate-500"
            >Suggested Resources</label
          >
          <textarea
            :class="textareaClass"
            rows="4"
            :disabled="!editable"
            :value="risk.suggested_resources ?? ''"
            @input="updateField('suggested_resources', $event.target.value)"
          ></textarea>
        </div>
        <div>
          <label class="block text-xs text-slate-500"
            >Resource Justification</label
          >
          <textarea
            :class="textareaClass"
            rows="4"
            :disabled="!editable"
            :value="risk.resource_justification ?? ''"
            @input="updateField('resource_justification', $event.target.value)"
          ></textarea>
        </div>
      </div>
    </FormBox>

    <FormBox header="Final Notes">
      <textarea
        :class="textareaClass"
        rows="4"
        :disabled="!editable"
        :value="risk.final_notes ?? ''"
        @input="updateField('final_notes', $event.target.value)"
      ></textarea>
    </FormBox>

    <FormBox header="AI Risk Assessment">
      <template #nav>
        <button
          class="btn"
          type="button"
          :disabled="!editable"
          @click.prevent="openChatWithHistory"
        >
          Open Chat
        </button>
      </template>
      <div v-if="assessmentError" class="mb-2 text-sm text-rose-500">
        {{ assessmentError }}
      </div>
      <div v-else-if="assessmentLoading" class="mb-2 text-sm text-slate-500">
        Running assessment...
      </div>
      <textarea
        :class="textareaClass"
        rows="8"
        :disabled="!editable"
        :value="risk.ai_assessment ?? ''"
        @input="updateField('ai_assessment', $event.target.value)"
        placeholder="AI-generated risk assessment summary"
      ></textarea>
    </FormBox>
  </div>

  <Teleport to="body">
    <div
      v-if="chatOpen"
      ref="chatWindowRef"
      class="fixed z-50 h-full w-full sm:h-auto sm:w-96"
      :style="{
        left: chatPosition.x !== null ? `${chatPosition.x}px` : 'auto',
        top: chatPosition.y !== null ? `${chatPosition.y}px` : 'auto',
      }"
    >
      <div
        class="flex h-full flex-col bg-white shadow-xl dark:bg-slate-900 sm:h-auto sm:rounded-lg sm:border sm:border-slate-200 sm:dark:border-slate-700"
      >
        <div
          class="flex items-center justify-between border-b border-slate-200 p-4 dark:border-slate-700 sm:cursor-move"
          @mousedown.prevent="onDragStart"
        >
          <div>
            <h3
              class="text-lg font-semibold text-slate-900 dark:text-slate-100"
            >
              AI Risk Assessment
            </h3>
            <p class="text-xs text-slate-500">
              Purple Guide aware assessment with extracted fields.
            </p>
          </div>
          <div class="flex items-center gap-2">
            <button
              class="btn"
              type="button"
              :disabled="!editable || chatLoading || assessmentLoading"
              @click.prevent="rescanAssessment"
              aria-label="Rescan assessment"
            >
              <Icon.ArrowPathIcon class="icon" />
            </button>
            <button
              class="btn"
              type="button"
              @click.prevent="closeChat"
              aria-label="Close"
            >
              <Icon.XMarkIcon class="icon" />
            </button>
          </div>
        </div>

        <div ref="chatScrollRef" class="min-h-0 flex-1 overflow-y-auto p-4 space-y-4 sm:h-80 sm:flex-none">
          <div v-if="!chatMessages.length" class="text-sm text-slate-500">
            Ask the assistant to review ticket details and propose medical cover
            with a Purple Guide tier.
          </div>
          <div
            v-for="(message, index) in chatMessages"
            :key="message.id ?? index"
            class="space-y-2"
          >
            <div class="text-xs uppercase tracking-wide text-slate-400">
              {{ message.role === "assistant" ? "Assistant" : "You" }}
            </div>
            <div
              class="rounded-lg border border-slate-200 dark:border-slate-700 p-3 text-sm whitespace-pre-wrap text-slate-800 dark:text-slate-100"
            >
              {{ message.content }}
            </div>
          </div>
        </div>

        <div
          class="border-t border-slate-200 dark:border-slate-700 p-4 space-y-3"
        >
          <div v-if="chatError" class="text-sm text-rose-500">
            {{ chatError }}
          </div>
          <textarea
            :class="textareaClass"
            rows="3"
            v-model="chatInput"
            :disabled="!editable"
            @keydown="handleChatKeydown"
            placeholder="Ask for a Purple Guide risk assessment..."
          ></textarea>
          <div class="flex items-center justify-between">
            <span v-if="chatLoading" class="text-sm text-slate-500"
              >Thinking...</span
            >
            <span v-else class="text-xs text-slate-400"
              >Response will populate AI assessment and relevant fields.</span
            >
            <button
              class="btn accent"
              type="button"
              :disabled="!editable || chatLoading || !chatInput.trim()"
              @click.prevent="sendMessage()"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
